import { Box, Grid } from '@chakra-ui/react';
import { Response } from 'datx-jsonapi';
import NextLink from 'next/link';
import { FC, useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import { NoContentAvailable } from '@/components/ui/features/events-list/EventListGrid/components/NoContentAvailable';
import { EventCardFallback, PastEventCard, UpcomingEventCard } from '@/components/ui/shared/event/EventCard/EventCard';
import { HubsPageActions } from '@/components/ui/shared/hubs/HubsPageActions/HubsPageActions';
import { eventSortingOptions } from '@/constants/event-sorting-options';
import { EventListStatus } from '@/enums/EventListStatus';
import { EventSort } from '@/enums/EventSort';
import useElement from '@/hooks/useElement';
import { StatusStringParam, StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { useResourcesInfinite } from '@/lib/datx-jsonapi-react';
import { queryThinEventsList } from '@/queries/thin-events';
import { ThinEvent } from '@/resources/ThinEvent';
import { getEventHref } from '@/utils/getEventHref';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';

import { BackToTop } from '../../../core/BackToTop/BackToTop';

interface IUrlQueryStringParams {
  query: string;
  subjectId: string;
  conferenceId: string;
  status: EventListStatus;
  sort: EventSort;
}

export const TemporaryEventListGrid: FC = () => {
  const { element: eventListElement, ref: eventListRef } = useElement();

  const { subjectId, conferenceId, sort, query, status } = useQueryParams<IUrlQueryStringParams>({
    query: StringParam,
    subjectId: StringParam,
    conferenceId: StringParam,
    status: StatusStringParam,
    sort: StringParam,
  });

  const filters = {
    subject_id: subjectId,
    conference_id: conferenceId,
    query,
    scope: query ? EventListStatus.all : status,
  };

  const { data: events, error, size, setSize } = useResourcesInfinite(queryThinEventsList(filters, sort));

  const { isLoadingMore, isReachingEnd, items, isEmpty, meta } = useMemo(
    () => extractSWRInfiniteData<ThinEvent>(events as Array<Response<ThinEvent>>, error, size),
    [events, error, size]
  );

  const activeStatus = EventListStatus[status] || EventListStatus.live;
  const defaultOption = !query && status === EventListStatus.upcoming ? eventSortingOptions[3] : eventSortingOptions[2];

  return (
    <Box position="relative" ref={eventListRef}>
      <HubsPageActions
        sortingOptions={eventSortingOptions}
        defaultOption={defaultOption.value}
        totalCount={meta?.totalCount}
      />
      <Grid mt={6} templateColumns="repeat(auto-fill, minmax(min(100%, 324px), 1fr))" gap="8">
        {items.map((event) => {
          if (status === EventListStatus.past) {
            return (
              <NextLink key={event.id} href={getEventHref(event)} passHref legacyBehavior>
                <PastEventCard as="a" event={event} />
              </NextLink>
            );
          } else {
            return (
              <NextLink key={event.id} href={getEventHref(event)} passHref legacyBehavior>
                <UpcomingEventCard as="a" event={event} />
              </NextLink>
            );
          }
        })}

        {isLoadingMore ? Array.from({ length: 10 }, (_, i) => <EventCardFallback key={i} />) : null}
      </Grid>

      <InView
        onChange={(inView) => {
          if (inView && !error && !isReachingEnd) {
            setSize(size + 1);
          }
        }}
      >
        <span />
      </InView>

      {isEmpty && <NoContentAvailable activeStatus={activeStatus} query={query} />}

      {!isEmpty && <BackToTop parentTopOffset={eventListElement?.offsetTop} />}
    </Box>
  );
};
